import { useEffect, useState } from 'react';
import { useAuth } from '@chainlit/react-client';
import { Navigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, MessageSquareText, History } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';

import { orgApi, type Org, type OrgKB } from '@/api/organizations';
import {
  systemPromptsApi, type SystemPrompt, type SystemPromptAudit,
} from '@/api/systemPrompts';
import { useAppConfig } from '@/hooks/useAppConfig';

export default function SystemPromptsPage() {
  const { user: me } = useAuth();
  const appConfig = useAppConfig();
  const myRole = (me?.metadata as Record<string, unknown>)?.role as string | undefined;
  // 權限守衛獨立成外層元件：內層 Body 的 hooks 因此不會出現「早退 return
  // 之後才宣告」的順序問題。設定載入完成前不渲染——單機版免登入（role
  // undefined），若在載入前判斷會被誤導向回聊天頁。
  if (!appConfig) return null;
  if (!appConfig.single_user_mode && myRole !== 'super_admin') {
    return <Navigate replace to="/chat" />;
  }
  return <SystemPromptsBody />;
}

function SystemPromptsBody() {
  // ── 選擇器：業者 → 知識庫 ──
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [kbs, setKbs] = useState<OrgKB[]>([]);
  const [selectedKbId, setSelectedKbId] = useState('');

  // ── Prompt 清單 ──
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Create / Edit 對話框 ──
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SystemPrompt | null>(null);
  const [formName, setFormName] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formDefault, setFormDefault] = useState(false);
  const [formActive, setFormActive] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // ── Delete ──
  const [deleteTarget, setDeleteTarget] = useState<SystemPrompt | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── Audit ──
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditList, setAuditList] = useState<SystemPromptAudit[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    orgApi.list().then(setOrgs).catch((e) => setError(e.message));
  }, []);

  // 選業者 → 載入該業者已指派的知識庫
  useEffect(() => {
    setSelectedKbId('');
    setKbs([]);
    setPrompts([]);
    if (!selectedOrgId) return;
    orgApi.getKbs(selectedOrgId)
      .then((list) => setKbs(list.filter((k) => k.assigned)))
      .catch((e) => setError(e.message));
  }, [selectedOrgId]);

  const fetchPrompts = () => {
    if (!selectedKbId) return;
    setLoading(true);
    setError('');
    systemPromptsApi.list(selectedKbId, { includeInactive: true, orgId: selectedOrgId })
      .then(setPrompts)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  // 選知識庫 → 載入 Prompt
  useEffect(() => {
    if (selectedKbId) fetchPrompts();
    else setPrompts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKbId]);

  // ── Create / Edit ──
  const openCreate = () => {
    setEditTarget(null);
    setFormName(''); setFormContent(''); setFormDesc('');
    setFormDefault(false); setFormActive(true); setFormError('');
    setFormOpen(true);
  };

  const openEdit = (p: SystemPrompt) => {
    setEditTarget(p);
    setFormName(p.name); setFormContent(p.content); setFormDesc(p.description);
    setFormDefault(p.is_default); setFormActive(p.is_active); setFormError('');
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!formName.trim()) { setFormError('名稱不可為空'); return; }
    if (!formContent.trim()) { setFormError('提示詞內容不可為空'); return; }
    setFormLoading(true);
    try {
      if (editTarget) {
        await systemPromptsApi.update(editTarget.id, {
          name: formName.trim(),
          content: formContent,
          description: formDesc,
          is_default: formDefault,
          is_active: formActive,
        });
      } else {
        await systemPromptsApi.create({
          kb_id: selectedKbId,
          org_id: selectedOrgId,
          name: formName.trim(),
          content: formContent,
          description: formDesc,
          is_default: formDefault,
        });
      }
      setFormOpen(false);
      fetchPrompts();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setFormLoading(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await systemPromptsApi.delete(deleteTarget.id);
      setPrompts((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Audit ──
  const openAudit = () => {
    setAuditOpen(true);
    setAuditLoading(true);
    systemPromptsApi.audit({ kbId: selectedKbId, orgId: selectedOrgId, limit: 100 })
      .then(setAuditList)
      .catch(() => setAuditList([]))
      .finally(() => setAuditLoading(false));
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">系統提示詞管理</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openAudit} disabled={!selectedKbId}>
            <History className="size-4 mr-1" />稽核紀錄
          </Button>
          <Button
            variant="outline"
            className="border-primary text-primary hover:bg-primary/10"
            onClick={openCreate}
            disabled={!selectedKbId}
          >
            <Plus className="size-4 mr-1" />新增提示詞
          </Button>
        </div>
      </div>

      {/* 業者 + 知識庫 選擇 */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
          <SelectTrigger className="w-56"><SelectValue placeholder="選擇業者" /></SelectTrigger>
          <SelectContent>
            {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedKbId} onValueChange={setSelectedKbId} disabled={!selectedOrgId}>
          <SelectTrigger className="w-56"><SelectValue placeholder="選擇知識庫" /></SelectTrigger>
          <SelectContent>
            {kbs.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <Loader2 className="size-5 animate-spin" /><span>載入中…</span>
        </div>
      )}
      {!loading && error && <div className="text-center py-8 text-red-500 text-sm">{error}</div>}

      {!loading && !error && selectedKbId && (
        prompts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageSquareText className="size-12 mx-auto mb-3 opacity-40" />
            <p>此知識庫尚無提示詞，點擊右上角新增</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名稱</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>更新時間</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {prompts.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">
                      {p.name}
                      {p.description && (
                        <p className="text-xs text-muted-foreground font-normal">{p.description}</p>
                      )}
                    </TableCell>
                    <TableCell className="space-x-1">
                      {p.is_default && (
                        <Badge className="bg-primary/15 text-primary border-primary/30 text-xs">預設</Badge>
                      )}
                      {p.is_active
                        ? <Badge variant="outline" className="text-xs">啟用</Badge>
                        : <Badge variant="outline" className="text-xs text-muted-foreground">停用</Badge>}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(p.updated_at).toLocaleString('zh-TW')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => setDeleteTarget(p)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editTarget ? '編輯提示詞' : '新增提示詞'}</DialogTitle>
            <DialogDescription>系統提示詞會綁定目前選定的業者與知識庫</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">名稱</label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="例：客服標準回覆指令" />
            </div>
            <div>
              <label className="text-sm font-medium">提示詞內容</label>
              <Textarea
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder="輸入 System Prompt 內容…"
                className="min-h-40 font-mono text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium">描述（選填）</label>
              <Input value={formDesc} onChange={(e) => setFormDesc(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={formDefault} onCheckedChange={(v) => setFormDefault(!!v)} />
              <span className="text-sm">設為此知識庫的預設提示詞</span>
            </label>
            {editTarget && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={formActive} onCheckedChange={(v) => setFormActive(!!v)} />
                <span className="text-sm">啟用（停用後不在選單顯示）</span>
              </label>
            )}
            {formError && <p className="text-sm text-red-500">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>取消</Button>
            <Button onClick={handleSubmit} disabled={formLoading}>
              {formLoading && <Loader2 className="size-4 mr-1 animate-spin" />}
              {editTarget ? '儲存' : '建立'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除提示詞「{deleteTarget?.name}」嗎？已開啟的對話因保有快照不受影響，此操作無法復原。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteLoading} className="bg-red-600 hover:bg-red-700">
              {deleteLoading && <Loader2 className="size-4 mr-1 animate-spin" />}刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit Dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>提示詞稽核紀錄</DialogTitle>
            <DialogDescription>此公司在此知識庫下所有提示詞的異動歷史（含修改前後內容）</DialogDescription>
          </DialogHeader>
          <div className="py-2 max-h-[60vh] overflow-y-auto space-y-3">
            {auditLoading && (
              <div className="flex items-center gap-2 text-muted-foreground py-4 justify-center">
                <Loader2 className="size-4 animate-spin" /><span>載入中…</span>
              </div>
            )}
            {!auditLoading && auditList.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">尚無異動紀錄</p>
            )}
            {!auditLoading && auditList.map((a) => {
              const actionLabel =
                a.action === 'create' ? { text: '新增', cls: 'bg-green-500/15 text-green-600 border-green-500/30' }
                : a.action === 'update' ? { text: '修改', cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30' }
                : a.action === 'delete' ? { text: '刪除', cls: 'bg-red-500/15 text-red-600 border-red-500/30' }
                : { text: a.action, cls: '' };
              return (
                <div key={a.id} className="border border-border rounded-md p-3 text-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-xs ${actionLabel.cls}`}>{actionLabel.text}</Badge>
                      <span className="font-medium">{a.new_name || a.old_name || '（已刪除）'}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      操作者：{a.actor_username || '（未知）'} ・ {new Date(a.created_at).toLocaleString('zh-TW')}
                    </span>
                  </div>

                  {/* 白話對照：新增→內容；修改→修改前/後；刪除→刪除前內容 */}
                  {a.action === 'create' && a.new_content && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">內容</p>
                      <p className="bg-muted rounded p-2 whitespace-pre-wrap">{a.new_content}</p>
                    </div>
                  )}
                  {a.action === 'update' && (
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">修改前</p>
                        <p className="bg-red-500/10 rounded p-2 whitespace-pre-wrap">{a.old_content}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">修改後</p>
                        <p className="bg-green-500/10 rounded p-2 whitespace-pre-wrap">{a.new_content}</p>
                      </div>
                    </div>
                  )}
                  {a.action === 'delete' && a.old_content && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">刪除前內容</p>
                      <p className="bg-muted rounded p-2 whitespace-pre-wrap">{a.old_content}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)}>關閉</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
